<template>
  <teleport to="body">
    <transition name="sticker-overlay">
      <div v-if="modelValue" class="sticker-overlay" @click.self="close">
        <transition name="sticker-slide">
          <div v-if="modelValue" class="sticker-panel">
            <!-- 탭 영역 -->
            <div class="sticker-tabs">
              <div class="tab-scroll">
                <button
                  v-for="(tab, i) in tabs"
                  :key="i"
                  :class="['tab-item', { active: activeTab === i }]"
                  @click="activeTab = i"
                >
                  <span v-if="tab.label">{{ tab.label }}</span>
                  <img v-else :src="tab.icon" :alt="tab.name" />
                </button>
              </div>
            </div>

            <!-- 스티커 그리드 -->
            <div class="sticker-content" ref="contentRef">
              <div class="sticker-grid">
                <template v-if="activeTab === 0">
                  <!-- 전체 탭: 모든 카테고리 표시 -->
                  <div v-for="(cat, ci) in categories" :key="ci" class="category-section">
                    <div class="category-label">{{ cat.name }}</div>
                    <div class="grid">
                      <img
                        v-for="(url, si) in cat.items"
                        :key="si"
                        :src="url"
                        loading="lazy"
                        @click="select(url)"
                      />
                    </div>
                  </div>
                </template>
                <template v-else>
                  <!-- 개별 탭 -->
                  <div class="grid">
                    <img
                      v-for="(url, si) in categories[activeTab - 1]?.items"
                      :key="si"
                      :src="url"
                      loading="lazy"
                      @click="select(url)"
                    />
                  </div>
                </template>
              </div>
            </div>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue'

const props = defineProps({
  modelValue: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue', 'select'])

const activeTab = ref(0)
const contentRef = ref(null)

watch(() => props.modelValue, (v) => {
  if (v) activeTab.value = 0
})

watch(activeTab, () => {
  if (contentRef.value) contentRef.value.scrollTop = 0
})

function close() {
  emit('update:modelValue', false)
}

function select(url) {
  emit('select', url)
  close()
}

// ── 스티커 데이터 ──
const BASE = 'https://amadiary.com/pub/edit_icon/'

const categories = [
  {
    name: 'G',
    icon: BASE + 'chara_G_1.png',
    items: [
      'chara_G_4.png','chara_G_angry.png','chara_G_hehe.png','chara_G_oops.png','chara_G_wtf.png',
      'chara_dontworry.png','chara_joayo.png','chara_letsgo.png','chara_motorcycle.png','chara_parachute.png',
      'chara_G_1.png','chara_G_2.png','chara_G_3.png','sche_chara_g01.png','sche_chara_g02.png',
      'sche_chara_blackeye.png','sche_chara_poop.png','chara_lionsound.png','chara_gguck.png','chara_shock.png',
      'chara_catch.png','chara_haha.png',
    ].map(f => BASE + f)
  },
  {
    name: 'T',
    icon: BASE + 'chara_T_1.png',
    items: [
      'chara_T_ein.png','chara_T_dontmove.png','chara_cool.png','chara_ddakdae.png','chara_why.png',
      'chara_wthell.png','chara_linger.png','chara_T_1.png','chara_T_2.png','chara_T_3.png',
      'sche_chara_crow01.png','sche_chara_t01.png','sche_chara_t02.png','sche_chara_eyehide.png','sche_chara_readbook.png',
      'chara_waky.png','chara_guitar.png','chara_birthday_pic.png','chara_hehe.png','chara_raining.png','chara_mugshot.png',
    ].map(f => BASE + f)
  },
  {
    name: 'S',
    icon: BASE + 'chara_S_1.png',
    items: [
      'chara_S_masa.png','chara_amazing.png','chara_chamtree.png','chara_frozen.png','chara_magma.png',
      'chara_ohno.png','chara_okstop.png','chara_barbelcurl.png','chara_S_1.png','chara_S_2.png','chara_S_3.png',
      'sche_chara_question.png','sche_chara_bee.png','sche_chara_turtle01.png','sche_chara_s01.png','sche_chara_hardwork.png',
      'chara_running.png','chara_skate.png','chara_sigh.png','chara_sorry.png',
    ].map(f => BASE + f)
  },
  {
    name: 'E',
    icon: BASE + 'chara_E_1.png',
    items: [
      'chara_E_mau.png','chara_E_question.png','chara_E_tired.png','chara_E_zzz.png','chara_imsad.png',
      'chara_cakeonhead.png','chara_dungsil.png','chara_splits.png','chara_licklick.png',
      'chara_E_1.png','chara_E_2.png','chara_E_3.png','sche_chara_cat01.png','sche_chara_e01.png',
      'sche_chara_grgr.png','sche_chara_whip.png','chara_tablecrying.png','chara_present.png',
      'chara_shark_cowbow.png','chara_surprise.png','chara_upset.png',
    ].map(f => BASE + f)
  },
  {
    name: 'L',
    icon: BASE + 'chara_L_1.png',
    items: [
      'chara_L_maria.png','chara_flowerwalk.png','chara_happy2.png','chara_thankyou.png','chara_heckheck.png',
      'chara_L_1.png','chara_L_2.png','chara_L_3.png','sche_chara_dog02.png','sche_chara_dog01.png',
      'sche_chara_l01.png','sche_chara_l02.png','chara_overweight.png','chara_situp.png','chara_yammy.png',
      'chara_birthday_pic.png','chara_cheerup.png','chara_makeup2.png','chara_crazylove.png','chara_dissapoint.png','chara_yammy2.png',
    ].map(f => BASE + f)
  },
  {
    name: 'V',
    icon: BASE + 'chara_V_1.png',
    items: [
      'chara_V_dona.png','chara_V_comeon.png','chara_V_fucku.png','chara_nosedig.png','chara_drivingcar.png',
      'chara_fart.png','chara_slobber.png','chara_peekaboo.png','chara_V_1.png','chara_V_2.png','chara_V_3.png',
      'sche_chara_tent.png','sche_chara_hare.png','sche_chara_hare02.png','sche_chara_roma.png',
      'chara_joke.png','chara_kyia.png','chara_vacation.png','chara_run.png','chara_runningmachine.png',
      'chara_birthday_pic.png','chara_happy.png','chara_hungry.png','chara_face_after.png','chara_face_before.png',
      'chara_makeup.png','chara_sorry.png',
    ].map(f => BASE + f)
  },
  {
    name: 'C',
    icon: BASE + 'chara_C_2.png',
    items: [
      'chara_C_elly.png','chara_againfat.png','chara_cynical.png','chara_whhat.png','chara_thankyou2.png',
      'chara_C_2.png','chara_C_3.png','sche_chara_tent.png','sche_chara_c01.png','sche_chara_danaka.png',
      'sche_chara_heartattack.png','chara_marketing.png','chara_whathappen.png','chara_cryingsee.png',
      'chara_itsokay.png','chara_nervus.png','chara_singing.png','chara_rainday.png',
    ].map(f => BASE + f)
  },
  {
    name: 'O',
    icon: BASE + 'chara_O_1.png',
    items: [
      'chara_O_han.png','chara_ggon.png','chara_yomang.png','chara_sodung.png','chara_O_1.png','chara_O_2.png','chara_O_3.png',
      'sche_chara_talk.png','sche_chara_question.png','sche_chara_walk01.png','sche_chara_dig.png',
      'chara_charge.png','chara_dance.png','chara_love_u.png','chara_threelaugh.png','chara_angry.png',
      'chara_slap.png','chara_golfing.png','chara_moneybag.png','chara_pary.png',
    ].map(f => BASE + f)
  },
  {
    name: '기타',
    icon: BASE + 'sche_chara_shot.png',
    items: [
      'chara_greed.png','chara_hatemorning.png','chara_shit1.png','chara_shit2.png','chara_trustme.png',
      'chara_waste.png','chara_whereami.png','sche_chara_slime01.png','sche_chara_slime02.png','sche_chara_slime03.png',
      'sche_chara_shot.png','sche_chara_mad.png','sche_chara_light.png','sche_chara_lie.png',
      'chara_shut.png','chara_arrest.png','chara_fuckyou.png','chara_scaryout.png',
    ].map(f => BASE + f)
  },
  {
    name: '데코',
    icon: BASE + 'deco_thumbup.png',
    items: [
      'sche_chara_paw.png','sche_chara_duck02.png','sche_chara_duck01.png','sche_chara_chicken.png',
      'sche_chara_catcap.png','sche_chara_catnose.png','sche_chara_crow.png','sche_chara_dogear.png',
      'sche_chara_eliglass.png','sche_chara_eye.png','sche_chara_hareear.png','sche_chara_meereye.png',
      'sche_chara_turtleband.png','deco_fall.png','deco_thumblow.png','deco_dokidoki.png','deco_money.png',
      'deco_thumbup.png','deco_drops.png','deco_dalgona01.png','deco_dalgona02.png','deco_em02.png','deco_em03.png',
      'deco_ex02.png','deco_heart.png','deco_heart02.png','deco_heart_three.png','deco_hearts03.png',
      'deco_mad.png','deco_quest02.png','deco_skull.png','deco_star.png','deco_star02.png',
      'deco_sungl.png','deco_sungl02.png','deco_v.png',
      'sche_love_lip.png','sche_love_key.png','sche_love_heart02.png','sche_love_heart01.png','sche_love_bukke.png',
      'sche_love_broken02.png','sche_love_broken.png',
      'sche_food_stake.png','sche_food_soju.png','sche_food_popcorn.png','sche_food_pizza02.png','sche_food_pizza01.png',
      'sche_food_koreanstake.png','sche_food_fry.png','sche_food_egg.png','sche_food_drink.png','sche_food_coffe.png',
      'sche_food_choco.png','sche_food_chicken.png','sche_food_candy.png','sche_food_cake.png','sche_food_cake02.png',
      'sche_food_cake03.png','sche_food_burger02.png','sche_food_burger01.png','sche_food_brad02.png','sche_food_brad01.png',
      'sche_food_berry.png','sche_food_beer.png','sche_food_apple.png','sche_food_ramen.png','sche_food_rise.png','sche_food_zzige.png',
      'sche_nature_weather05.png','sche_nature_weather04.png','sche_nature_weather03.png','sche_nature_weather02.png','sche_nature_weather01.png',
      'sche_nature_tree03.png','sche_nature_tree02.png','sche_nature_tree01.png','sche_nature_plant02.png','sche_nature_plant.png',
      'sche_nature_flower02.png','sche_nature_flower.png','sche_nature_fire.png',
      'sche_health_tooth.png','sche_health_tenis.png','sche_health_medi.png','sche_health_golf.png','sche_health_box.png',
      'sche_health_bicycle.png','sche_health_band.png','sche_health_ball03.png','sche_health_ball02.png','sche_health_ball01.png',
      'sche_health_arm.png','sche_health_swim.png','sche_health_baseball.png',
      'sche_living_wash02.png','sche_living_wash01.png','sche_living_ticket.png','sche_living_success.png','sche_living_socks.png',
      'sche_living_school.png','sche_living_roo.png','sche_living_note.png','sche_living_money.png','sche_living_figure.png',
      'sche_living_cloth.png','sche_living_clean03.png','sche_living_clean02.png','sche_living_clean01.png','sche_living_bell.png',
      'sche_living_battery02.png','sche_living_battery01.png','sche_living_apart.png',
      'sche_emo_wow06.png','sche_emo_wow05.png','sche_emo_wow04.png','sche_emo_wow03.png','sche_emo_wow02.png','sche_emo_wow01.png',
      'sche_emo_shy07.png','sche_emo_shy06.png','sche_emo_shy05.png','sche_emo_shy04.png','sche_emo_shy03.png','sche_emo_shy02.png','sche_emo_shy01.png',
      'sche_emo_sad02.png','sche_emo_sad01.png',
      'sche_emo_mad05.png','sche_emo_mad04.png','sche_emo_mad03.png','sche_emo_mad02.png','sche_emo_mad01.png',
      'sche_emo_lol06.png','sche_emo_lol05.png','sche_emo_lol04.png','sche_emo_lol03.png','sche_emo_lol02.png','sche_emo_lol01.png',
      'sche_emo_hungry03.png','sche_emo_hungry02.png','sche_emo_hungry01.png',
      'sche_emo_cheer03.png','sche_emo_cheer02.png','sche_emo_cheer01.png',
      'sche_emo_basic06.png','sche_emo_basic05.png','sche_emo_basic04.png','sche_emo_basic03.png','sche_emo_basic02.png','sche_emo_basic01.png',
    ].map(f => BASE + f)
  },
  {
    name: '텍스트',
    icon: BASE + 'sche_text_i.png',
    items: [
      'deco_shutup.png','text_gazua.png','text_lol.png','text_yolo.png','text_fighting.png','text_nadegima.png',
      'text_ghak.png','text_start.png','text_ssdam.png','text_loveu_02.png','text_joke.png','text_cute.png','text_alone.png',
      'sche_text_shit.png','sche_text_shaking.png','sche_text_love.png','sche_text_kingry.png','sche_text_jmt.png',
      'sche_text_i.png','sche_text_hardwork.png','sche_text_happy.png','sche_text_good.png','sche_text_good2.png',
      'sche_text_good3.png','sche_text_cool.png','sche_text_cheer.png','sche_text_cafe.png','sche_text_birth.png',
      'sche_text_backhome.png',
      'b_01.png','b_02.png','b_03.png','b_04.png','b_05.png','b_06.png','b_07.png','b_08.png','b_09.png','b_10.png','b_11.png',
    ].map(f => BASE + f)
  },
  {
    name: '이모지',
    icon: BASE + 'sche_emo_cheer02.png',
    items: [
      'sche_emo_wow06.png','sche_emo_wow05.png','sche_emo_wow04.png','sche_emo_wow03.png','sche_emo_wow02.png','sche_emo_wow01.png',
      'sche_emo_shy07.png','sche_emo_shy06.png','sche_emo_shy05.png','sche_emo_shy04.png','sche_emo_shy03.png','sche_emo_shy02.png','sche_emo_shy01.png',
      'sche_emo_sad02.png','sche_emo_sad01.png',
      'sche_emo_mad05.png','sche_emo_mad04.png','sche_emo_mad03.png','sche_emo_mad02.png','sche_emo_mad01.png',
      'sche_emo_lol06.png','sche_emo_lol05.png','sche_emo_lol04.png','sche_emo_lol03.png','sche_emo_lol02.png','sche_emo_lol01.png',
      'sche_emo_hungry03.png','sche_emo_hungry02.png','sche_emo_hungry01.png',
      'sche_emo_cheer03.png','sche_emo_cheer02.png','sche_emo_cheer01.png',
      'sche_emo_basic06.png','sche_emo_basic05.png','sche_emo_basic04.png','sche_emo_basic03.png','sche_emo_basic02.png','sche_emo_basic01.png',
    ].map(f => BASE + f)
  },
]

// 탭 목록 생성
const tabs = [
  { label: '전체', name: '전체' },
  ...categories.map(c => ({ icon: c.icon, name: c.name }))
]
</script>

<style scoped>
.sticker-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: var(--color-surface-deep);
  backdrop-filter: blur(var(--effect-blur));
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.sticker-panel {
  width: 100%;
  max-width: 600px;
  height: 45vh;
  min-height: 320px;
  max-height: 500px;
  background: var(--color-background);
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.2);
}

/* ── 탭 ── */
.sticker-tabs {
  flex-shrink: 0;
  border-bottom: 1px solid var(--color-background-light);
  padding: 0 8px;
}
.tab-scroll {
  display: flex;
  overflow-x: auto;
  gap: 4px;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding: 8px 0;
}
.tab-scroll::-webkit-scrollbar { display: none; }

.tab-item {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: none;
  background: none;
  border-radius: var(--radius-md);
  cursor: pointer;
  padding: 4px;
  transition: background 0.15s;
  border-bottom: 2px solid transparent;
  color: var(--color-text-primary);
}
.tab-item:hover { background: var(--color-surface-reverse); }
.tab-item.active {
  border-bottom-color: var(--color-brand);
  background: var(--color-surface-reverse);
}
.tab-item span {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-bold);
  white-space: nowrap;
}
.tab-item img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

/* ── 콘텐츠 ── */
.sticker-content {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;
  -webkit-overflow-scrolling: touch;
}

.category-section {
  margin-bottom: 12px;
}
.category-label {
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
  padding: 4px 6px 8px;
  position: sticky;
  top: 0;
  background: var(--color-background);
  z-index: 1;
}

.grid {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
}
.grid img {
  width: 100%;
  aspect-ratio: 1;
  object-fit: contain;
  cursor: pointer;
  padding: 4px;
  border-radius: var(--radius-md);
  transition: transform 0.12s, background 0.12s;
}
.grid img:hover {
  background: var(--color-surface-reverse);
  transform: scale(1.08);
}
.grid img:active {
  transform: scale(0.95);
}

/* ── 반응형 ── */
@media (max-width: 768px) {
  .sticker-panel {
    max-width: 100%;
    height: 50vh;
    min-height: 280px;
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  }
  .grid {
    grid-template-columns: repeat(5, 1fr);
    gap: 4px;
  }
  .tab-item {
    width: 40px;
    height: 40px;
  }
  .tab-item img {
    width: 28px;
    height: 28px;
  }
}

@media (max-width: 480px) {
  .sticker-panel {
    height: 55vh;
    min-height: 260px;
  }
  .grid {
    grid-template-columns: repeat(4, 1fr);
    gap: 3px;
  }
  .grid img {
    padding: 3px;
  }
}

/* ── 트랜지션 ── */
.sticker-overlay-enter-active,
.sticker-overlay-leave-active {
  transition: opacity 0.25s ease;
}
.sticker-overlay-enter-from,
.sticker-overlay-leave-to {
  opacity: 0;
}

.sticker-slide-enter-active {
  transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}
.sticker-slide-leave-active {
  transition: transform 0.2s ease-in;
}
.sticker-slide-enter-from,
.sticker-slide-leave-to {
  transform: translateY(100%);
}
</style>
